import { FC } from 'react'
import { useWallet } from '@solana/wallet-adapter-react'
import type { Adapter } from '@solana/wallet-adapter-base'

// Deeplink URL patterns for popular Solana mobile wallets
const WALLET_DEEPLINKS: Record<string, (url: string) => string> = {
  'Phantom': (url: string) => {
    const encoded = encodeURIComponent(url)
    return `https://phantom.app/ul/browse/${encoded}?ref=${encodeURIComponent(window.location.origin)}`
  },
  'Solflare': (url: string) => {
    const encoded = encodeURIComponent(url)
    return `https://solflare.com/ul/v1/browse/${encoded}?ref=${encodeURIComponent(window.location.origin)}`
  },
  'Coinbase Wallet': (url: string) => {
    const encoded = encodeURIComponent(url)
    return `https://go.cb-w.com/dapp?cb_url=${encoded}`
  },
  'Trust': (url: string) => {
    const encoded = encodeURIComponent(url)
    return `https://link.trustwallet.com/open_url?coin_id=501&url=${encoded}`
  },
}

// Wallets that support mobile deeplinks
const SUPPORTED_MOBILE_WALLETS = Object.keys(WALLET_DEEPLINKS)

interface MobileWalletPickerProps {
  isOpen: boolean
  onClose: () => void
}

export const MobileWalletPicker: FC<MobileWalletPickerProps> = ({
  isOpen,
  onClose,
}) => {
  const { wallets } = useWallet()
  
  if (!isOpen) return null

  const currentUrl = window.location.href

  // Filter wallets that have mobile deeplink support
  const mobileWallets = wallets.filter((wallet) => 
    SUPPORTED_MOBILE_WALLETS.includes(wallet.adapter.name)
  )

  const handleWalletClick = (adapter: Adapter) => {
    const getDeeplink = WALLET_DEEPLINKS[adapter.name]
    if (getDeeplink) {
      const deeplink = getDeeplink(currentUrl)
      window.location.href = deeplink
    }
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-40 animate-fade-in"
        onClick={onClose}
      />

      {/* Modal - slides up from bottom */}
      <div className="fixed inset-x-0 bottom-0 z-50 animate-slide-up">
        <div className="bg-white rounded-t-2xl overflow-hidden max-w-lg mx-auto">
          {/* Handle bar */}
          <div className="flex justify-center pt-3 pb-2">
            <div className="w-10 h-1 bg-[#e5e7eb] rounded-full" />
          </div>

          {/* Header */}
          <div className="px-6 pb-4">
            <h2 className="text-lg font-semibold text-[#111827]">
              Open in wallet
            </h2>
            <p className="text-sm text-[#6b7280] mt-1">
              Choose a wallet to continue the payment
            </p>
          </div>

          {/* Wallet list */}
          <div className="px-4 pb-6 space-y-2">
            {mobileWallets.map((wallet) => (
              <button
                key={wallet.adapter.name}
                onClick={() => handleWalletClick(wallet.adapter)}
                className="w-full flex items-center gap-4 p-4 bg-[#f9fafb] hover:bg-[#f3f4f6] rounded-xl transition-colors"
              >
                <div className="w-12 h-12 rounded-xl bg-white shadow-sm flex items-center justify-center overflow-hidden p-2">
                  <img
                    src={wallet.adapter.icon}
                    alt={wallet.adapter.name}
                    className="w-full h-full object-contain"
                  />
                </div>
                <div className="flex-1 text-left">
                  <span className="text-base font-medium text-[#111827]">
                    {wallet.adapter.name}
                  </span>
                </div>
                <svg
                  className="w-5 h-5 text-[#9ca3af]"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            ))}

            {mobileWallets.length === 0 && (
              <p className="text-center text-sm text-[#6b7280] py-4">
                No supported mobile wallets found
              </p>
            )}
          </div>

          {/* Cancel button */}
          <div className="px-4 pb-8">
            <button
              onClick={onClose}
              className="w-full py-4 text-base font-medium text-[#6b7280] hover:text-[#111827] transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slide-up {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.2s ease-out;
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </>
  )
}
